import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';

import CustomerFormModal from 'src/features/customers/components/forms/CustomerFormModal';
import { Customer } from 'src/features/customers/utils/types';
import { LIST_CUSTOMERS_RESOURCE } from 'src/features/customers/api/useListCustomers';
import { CUSTOMER_RESOURCE } from 'src/features/customers/api/useGetCustomer';
import { useUpdateCustomer } from 'src/features/customers/api/useUpdateCustomer';
import { customersMessages } from 'src/features/customers/utils/messages';

interface UpdateCustomerFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedCustomer: Customer;
    children?: React.ReactNode;
}

function UpdateCustomerFormModal<TData>({
                                                   className,
                                                   semantics,
                                                   children,
                                                   selectedCustomer,
                                               }: UpdateCustomerFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateCustomer({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_CUSTOMERS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [CUSTOMER_RESOURCE] });
            toast({
                title: formatMessage(customersMessages.updateCustomerSuccess),
                description: formatMessage(customersMessages.updateCustomerSuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <CustomerFormModal
            selectedCustomer={selectedCustomer}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, id: selectedCustomer.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(customersMessages.editCustomerSubtitle, {
                entity: selectedCustomer.name,
            })}
        >
            {children}
        </CustomerFormModal>
    );
}

export default UpdateCustomerFormModal;
